import { useState } from 'react'
import { api } from '../api/client'
import type { AccessCheck, PropertyAdminView } from '../api/types'

interface Props {
  config: PropertyAdminView
  busy: boolean
  run: <T>(action: () => Promise<T>, message: string) => Promise<T | null>
  onChanged: () => Promise<void>
}

/**
 * Service-account keys: what is on the host, and the address to hand each client.
 *
 * `client_email` is shown prominently because it *is* the onboarding step — the client
 * pastes it into GA4 → Admin → Property Access Management as a Viewer. Having it on
 * screen is the difference between adding a client in a browser and doing it over SSH.
 * It is not secret; the private key it sits beside never leaves the host.
 *
 * The access check below is separate and deliberately manual. A key can be present,
 * valid, and still read nothing, because Viewer is granted per property — the commonest
 * setup failure there is, and indistinguishable from success on the filesystem.
 */
export function Credentials({ config, busy, run, onChanged }: Props) {
  const [checking, setChecking] = useState<string | null>(null)
  const [checks, setChecks] = useState<Record<string, AccessCheck>>({})
  const [copied, setCopied] = useState<string | null>(null)

  const details = config.credential_details ?? []
  const usedBy = (filename: string) =>
    config.properties.filter((item) => item.credential_file === filename)

  async function onFile(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    // Reset the input so choosing the same file twice still fires a change event —
    // otherwise a rejected upload cannot be retried without picking something else.
    event.target.value = ''
    if (!file) return

    const text = await file.text()
    const saved = await run(() => api.uploadCredential(file.name, text), `${file.name} stored.`)
    if (saved) await onChanged()
  }

  async function check(propertyId: string) {
    setChecking(propertyId)
    try {
      const result = await api.checkPropertyAccess(propertyId)
      setChecks((current) => ({ ...current, [propertyId]: result }))
    } catch (cause) {
      setChecks((current) => ({
        ...current,
        [propertyId]: {
          property_id: propertyId,
          state: 'unavailable',
          detail: cause instanceof Error ? cause.message : 'The check could not run.',
        },
      }))
    } finally {
      setChecking(null)
    }
  }

  return (
    <>
      <section className="section" style={{ marginTop: 0 }}>
        <div className="section__head">
          <h2 className="section__title">Credentials</h2>
          <span className="section__note">Service-account keys on this host</span>
        </div>

        <div className="card admin__card">
          {details.length === 0 && (
            <p className="admin__empty">
              No service-account keys yet. Add one below, then point a property at it.
            </p>
          )}

          {details.map((item) => {
            const properties = usedBy(item.filename)
            return (
              <div className="admin__row admin__row--stacked" key={item.filename}>
                <div className="credential__head">
                  <code className="credential__file">{item.filename}</code>
                  {!item.valid && <span className="pill pill--critical">unusable</span>}
                  {item.valid && properties.length === 0 && <span className="pill">unused</span>}
                </div>

                {item.valid ? (
                  <>
                    <label className="field">
                      <span className="field__label">
                        Viewer address — the client adds this in GA4
                      </span>
                      <div className="credential__copy">
                        <input readOnly value={item.client_email} />
                        <button
                          type="button"
                          className="button secondary"
                          onClick={() => {
                            void navigator.clipboard.writeText(item.client_email)
                            setCopied(item.filename)
                          }}
                        >
                          {copied === item.filename ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                    </label>
                    <p className="admin__note">
                      Project <code>{item.project_id}</code> · updated {item.modified.slice(0, 10)}
                      {properties.length > 0 && (
                        <>
                          {' · used by '}
                          {properties
                            .map((property) => property.display_name || property.property_id)
                            .join(', ')}
                        </>
                      )}
                    </p>
                  </>
                ) : (
                  <p className="admin__note admin__note--critical">{item.problem}</p>
                )}

                <div className="actions">
                  <button
                    type="button"
                    className="button danger"
                    disabled={busy || properties.length > 0}
                    title={properties.length > 0 ? 'Repoint the properties using it first' : undefined}
                    onClick={async () => {
                      const removed = await run(
                        () => api.deleteCredential(item.filename),
                        `${item.filename} removed.`,
                      )
                      if (removed) await onChanged()
                    }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            )
          })}

          <div className="actions">
            <label className="button secondary">
              Add or replace a key
              <input
                type="file"
                accept="application/json,.json"
                style={{ display: 'none' }}
                disabled={busy}
                onChange={onFile}
              />
            </label>
          </div>
          <p className="admin__note">
            Uploading a file whose name already exists replaces it — which is how a key is
            rotated. The service account address is unchanged, so clients re-add nothing.
          </p>
        </div>
      </section>

      <section className="section settings-divider">
        <div className="section__head">
          <h2 className="section__title">Access check</h2>
          <span className="section__note">Costs a GA4 token — run it when you need it</span>
        </div>

        <div className="card admin__card">
          <p className="admin__note">
            A key can be present, valid, and still read nothing: the service account has to be
            added as a Viewer on each property separately. That is the commonest setup failure
            and it looks identical to success until something tries to fetch.
          </p>

          {config.properties.length === 0 && <p className="admin__empty">No properties configured yet.</p>}

          {config.properties.map((property) => {
            const result = checks[property.property_id]
            return (
              <div className="admin__row admin__row--stacked" key={property.property_id}>
                <div className="credential__head">
                  <strong>{property.display_name || property.property_id}</strong>
                  <code className="credential__file">{property.credential_file || 'default'}</code>
                  <button
                    type="button"
                    className="button secondary"
                    disabled={checking !== null}
                    onClick={() => void check(property.property_id)}
                  >
                    {checking === property.property_id ? 'Checking…' : 'Check access'}
                  </button>
                </div>
                {result && (
                  <p className={`admin__note${result.state === 'ok' ? '' : ' admin__note--critical'}`}>
                    {result.detail}
                  </p>
                )}
              </div>
            )
          })}
        </div>
      </section>
    </>
  )
}
